import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import API_ROUTES from "~/api-routes";
import useDateTime from "~/hooks/useDateTime";
import apiService from "~/services/service";
import { getStatusOptions, StatusKey } from "~/utils/status";

const useQuickSummary = ({ allEntities }) => {
  const [statusOverview, setStatusOverview] = useState({});
  const [kpiData, setKpiData] = useState({});
  const [kpiCardData, setKpiCardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isFetchingKpiData, setIsFetchingKpiData] = useState(false);
  const { dateTimeToTimezone } = useDateTime();
  const [duration, setDuration] = useState({ name: "Last 30 Days", code: 30 });
  const [isdKpiAllZero, setIsKpiAllZero] = useState(false);
  const [selectedValues, setSelectedValues] = useState({
    service_line: [],
    country: [],
    entities: [],
  });
  const entityservices = useSelector(
    (state) => state.globalEntities.entityservices,
  );
  const { access, role: userRole } = useSelector((state) => state.login);
  const [accessServices, setAccessServices] = useState([]);
  const [serviceSubOptions, setServiceSubOptions] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const { role } = useSelector((state) => state.login);
  const statusOptions = getStatusOptions([
    StatusKey.NOT_STARTED,
    StatusKey.IN_PROGRESS,
    StatusKey.COMPLETED,
    StatusKey.OVERDUE,
    StatusKey.FLAGGED,
  ]);
  useEffect(() => {
    if (entityservices) {
      const uniqueServices = [
        ...new Set((access || []).flatMap((item) => item.services)),
      ];
      const accessService =
        userRole !== "GXM_ADMIN"
          ? entityservices.filter((service) =>
              uniqueServices.includes(service.id),
            )
          : entityservices;

      const serviceSubOptions = accessService?.map((data) => {
        return {
          name: data?.name,
          code: data?.id,
        };
      });
      setServiceSubOptions(serviceSubOptions);
      setAccessServices(accessService);
    }
  }, [entityservices]);

  const [chipData, setChipData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    service_line: [],
    country: [],
    entities: [],
  });
  const [filter, setFilter] = useState({
    service_line: [],
    country: [],
    entities: [],
  });

  const handleFetchStatusOverview = async (
    value = 30,
    filterOption = filter,
  ) => {
    setLoading(true);
    try {
      const toDateLocal = new Date();
      toDateLocal.setHours(23, 59, 59, 999);
      const fromDateLocal = new Date();
      fromDateLocal.setDate(fromDateLocal.getDate() - value);
      fromDateLocal.setHours(0, 0, 0, 0);
      const from_date = dateTimeToTimezone({ date: fromDateLocal });
      const to_date = dateTimeToTimezone({ date: toDateLocal });
      const { data } = await apiService(
        API_ROUTES.PROCESS_STATUS_OVERVIEW,
        "POST",
        {
          body: {
            from_date,
            to_date,
            services: filterOption.service_line,
            entities: filterOption.entities,
            countries: filterOption.country,
            data_type: "process",
            stats_type: "statusOverview",
          },
        },
      );

      setStatusOverview(data?.data);
    } catch (error) {
      console.error("Error fetching process status overview:", error);
    } finally {
      setLoading(false);
    }
  };

  const defaultStatusCounts: Record<string, number> = {
    "not-started": 0,
    "in-progress": 0,
    completed: 0,
    overdue: 0,
    flagged: 0,
    "not-needed": 0,
  };

  const mergedStatusCounts = {
    ...defaultStatusCounts,
    ...statusOverview,
  };

  const filteredStatusOption = statusOptions.filter(
    (s) => mergedStatusCounts[s.code] > 0,
  );
  const allZero = statusOptions.every(
    (s) => !statusOverview[s.code] || statusOverview[s.code] === 0,
  );

  const data = {
    labels: allZero
      ? statusOptions.map((s) => s.name)
      : filteredStatusOption.map((s) => s.name),
    datasets: [
      {
        label: "Number of Processes",
        data: allZero
          ? statusOptions.map((s) => -1)
          : filteredStatusOption.map((s) => mergedStatusCounts[s.code]),
        backgroundColor: allZero
          ? statusOptions.map((s) => "#F4F4F4")
          : filteredStatusOption.map((s) => s.primary_color),
        borderWidth: 0,
        hoverOffset: 1,
      },
    ],
  };
  const isdChartAllZero = data?.datasets[0]?.data?.every((val) => val === -1);
  const isAllZero = (obj) => {
    return Object.values(obj).every(
      (item) => item.trend === 0 && item.data === 0,
    );
  };
  const fetchKpi = async (duration, filterOption = filter) => {
    setIsFetchingKpiData(true);
    const today = new Date();

    const currentFrom = new Date();
    currentFrom.setDate(today.getDate() - duration?.code);
    const currentTo = new Date(today);

    const previousFrom = new Date();
    previousFrom.setDate(today.getDate() - duration?.code * 2 + 1);
    const previousTo = new Date();
    previousTo.setDate(today.getDate() - duration?.code + 1);

    let kpis = {
      escalations: [
        {
          label: "current",
          from_date: dateTimeToTimezone({ date: currentFrom }),
          to_date: dateTimeToTimezone({ date: currentTo }),
          stats_type: "escalations",
        },
        {
          label: "previous",
          from_date: dateTimeToTimezone({ date: previousFrom }),
          to_date: dateTimeToTimezone({ date: previousTo }),
          stats_type: "escalations",
        },
      ],
      requests: [
        {
          label: "current",
          from_date: dateTimeToTimezone({ date: currentFrom }),
          to_date: dateTimeToTimezone({ date: currentTo }),
          stats_type: "requests",
        },
        {
          label: "previous",
          from_date: dateTimeToTimezone({ date: previousFrom }),
          to_date: dateTimeToTimezone({ date: previousTo }),
          stats_type: "requests",
        },
      ],
      timelinessOfDelivery: [
        {
          label: "current",
          from_date: dateTimeToTimezone({ date: currentFrom }),
          to_date: dateTimeToTimezone({ date: currentTo }),
          stats_type: "timelinessOfDelivery",
        },
        {
          label: "previous",
          from_date: dateTimeToTimezone({ date: previousFrom }),
          to_date: dateTimeToTimezone({ date: previousTo }),
          stats_type: "timelinessOfDelivery",
        },
      ],
      flagged: [
        {
          label: "current",
          from_date: dateTimeToTimezone({ date: currentFrom }),
          to_date: dateTimeToTimezone({ date: currentTo }),
          stats_type: "flagged",
        },
        {
          label: "previous",
          from_date: dateTimeToTimezone({ date: previousFrom }),
          to_date: dateTimeToTimezone({ date: previousTo }),
          stats_type: "flagged",
        },
      ],
      entitiesUnderManagement: [
        {
          label: "current",
          from_date: dateTimeToTimezone({ date: currentFrom }),
          to_date: dateTimeToTimezone({ date: currentTo }),
          stats_type: "entitiesUnderManagement",
        },
        {
          label: "previous",
          from_date: dateTimeToTimezone({ date: previousFrom }),
          to_date: dateTimeToTimezone({ date: previousTo }),
          stats_type: "entitiesUnderManagement",
        },
      ],
      requestAvgResolutionTime: [
        {
          label: "current",
          from_date: dateTimeToTimezone({ date: currentFrom }),
          to_date: dateTimeToTimezone({ date: currentTo }),
          stats_type: "requestAvgResolutionTime",
        },
        {
          label: "previous",
          from_date: dateTimeToTimezone({ date: previousFrom }),
          to_date: dateTimeToTimezone({ date: previousTo }),
          stats_type: "requestAvgResolutionTime",
        },
      ],
    };
    if (["LSP_USER", "LSP_MANAGER"].includes(role)) {
      delete kpis["escalations"];
    }

    const kpiResults = {};

    try {
      setIsKpiAllZero(false);
      for (const key in kpis) {
        const [current, previous] = kpis[key];

        const [currentRes, previousRes] = await Promise.all([
          apiService(API_ROUTES.PROCESS_STATUS_OVERVIEW, "POST", {
            body: {
              ...current,
              services: filterOption.service_line,
              entities: filterOption.entities,
              data_type: "process",
              countries: filterOption.country,
            },
          }),
          apiService(API_ROUTES.PROCESS_STATUS_OVERVIEW, "POST", {
            body: {
              ...previous,
              services: filterOption.service_line,
              entities: filterOption.entities,
              data_type: "process",
              countries: filterOption.country,
            },
          }),
        ]);

        const currentCount = currentRes?.data?.data || 0;
        const previousCount = previousRes?.data?.data || 0;

        kpiResults[key] = {
          current: currentCount,
          previous: previousCount,
        };
      }
      setKpiData(kpiResults);
      setIsFetchingKpiData(false);
    } catch (error) {
      console.error("Error fetching process status overview:", error);
    } finally {
      setIsFetchingKpiData(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKpi(duration);
    handleFetchStatusOverview();
  }, []);
  const getTrendPercentage = (current, previous) => {
    return Number(previous) !== 0
      ? Math.floor(
          ((Number(current) - Number(previous)) / Number(previous)) * 100,
        )
      : Math.floor(Number(current) * 100) || 0;
  };
  useEffect(() => {
    const cardData = {};
    if (Object.keys(kpiData).length) {
      for (const key in kpiData) {
        const { current, previous } = kpiData[key];
        if (key === "timelinessOfDelivery") {
          cardData[key] = {
            trend: Math.floor(current - previous) || 0,
            data: Math.floor(Number(current)) || 0,
          };
        } else if (key === "flagged") {
          cardData[key] = {
            trend: getTrendPercentage(current, previous) || 0,
            data: Number(current) || 0,
          };
        } else {
          cardData[key] = {
            trend: getTrendPercentage(current, previous) || 0,
            data: Number(current) || 0,
          };
        }
      }
      setKpiCardData(cardData);
      if (!isFetchingKpiData) {
        const isDataAvailable = isAllZero(cardData);
        setIsKpiAllZero(isDataAvailable);
      }
    }
  }, [kpiData]);

  useEffect(() => {
    if (isdChartAllZero && isdKpiAllZero) {
      setIsDataAvailable(true);
    } else {
      setIsDataAvailable(false);
    }
  }, [isdChartAllZero, isdKpiAllZero]);

  const handleSetDuration = (e) => {
    const { value } = e;
    setDuration(value);
    fetchKpi(value, filter);
    handleFetchStatusOverview(value.code, filter);
  };
  const handleChipData = (selectedValues) => {
    let chipArray = [];
    for (const key in selectedValues) {
      selectedValues[key].map((data) => {
        chipArray.push({ category: key, value: data });
      });
    }
    setChipData(chipArray);
  };

  const applyFilters = async () => {
    setIsFiltering(true);
    setFilter(selectedValues);
    handleChipData(selectedValues);
    await Promise.all([
      fetchKpi(duration, selectedValues),
      handleFetchStatusOverview(duration.code, selectedValues),
    ]);
    setIsFiltering(false);
  };

  const deSelectAll = async () => {
    const emptyData = {
      country: [],
      entities: [],
      service_line: [],
    };
    setIsFiltering(true);
    setFilter(emptyData);
    setSelectedFilters(emptyData);
    setSelectedValues(emptyData);
    setChipData([]);
    await Promise.all([
      fetchKpi(duration, emptyData),
      handleFetchStatusOverview(duration.code, emptyData),
    ]);
    setIsFiltering(false);
  };
  const handleRemoveFilter = async (data) => {
    const array = selectedValues[data.category]?.filter((value) => {
      return value !== data?.value;
    });

    delete selectedFilters[data?.value];
    setSelectedFilters(selectedFilters);
    const filteredValue = {
      ...selectedValues,
      [data.category]: array,
    };
    setFilter(filteredValue);
    handleChipData(filteredValue);
    setSelectedValues(filteredValue);
    setIsFiltering(true);
    await Promise.all([
      fetchKpi(duration, filteredValue),
      handleFetchStatusOverview(duration.code, filteredValue),
    ]);
    setIsFiltering(false);
  };
  const getService = (id) => {
    return entityservices?.find((data) => data?.id === id);
  };
  const getEntity = (id) => {
    return allEntities?.find((data) => data?.id === id);
  };

  const entitiesSubOptions = useMemo(() => {
    return allEntities?.map(({ id, name }) => ({ name, code: id })) || [];
  }, [allEntities]);

  const countryServiceSubOptions = useMemo(() => {
    return [...new Set(allEntities?.flatMap((e) => e.country) || [])];
  }, [allEntities]);
  const showFilter = useMemo(() => {
    const isOnlyOneOptionEach =
      countryServiceSubOptions.length === 1 &&
      entitiesSubOptions.length === 1 &&
      serviceSubOptions.length === 1;

    const isAnyMultiple =
      countryServiceSubOptions.length > 1 ||
      entitiesSubOptions.length > 1 ||
      serviceSubOptions.length > 1;

    if (entitiesSubOptions.length === 0) return false;

    if (isOnlyOneOptionEach) return false;
    if (isAnyMultiple) return true;

    return false;
  }, [entitiesSubOptions, countryServiceSubOptions, serviceSubOptions]);

  return {
    handleFetchStatusOverview,
    statusOverview,
    statusOptions,
    defaultStatusCounts,
    mergedStatusCounts,
    filteredStatusOption,
    data,
    loading,
    setLoading,
    kpiCardData,
    isFetchingKpiData,
    duration,
    setDuration,
    handleSetDuration,
    selectedFilters,
    setSelectedValues,
    setSelectedFilters,
    applyFilters,
    deSelectAll,
    chipData,
    entitiesSubOptions,
    serviceSubOptions,
    countryServiceSubOptions,
    handleRemoveFilter,
    getService,
    getEntity,
    showFilter,
    isdKpiAllZero,
    isFiltering,
    isDataAvailable,
  };
};

export default useQuickSummary;
